import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  Input
} from '@angular/core';
import {
  PopoverController,
  ModalController,
  RadioGroupCustomEvent
} from '@ionic/angular';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import {
  LanguageService,
  toTranslocoLang
} from 'src/app/services/language.service';
import { MsLocale } from 'src/app/models';
import { AppSelectors } from 'src/app/store/app/app.selectors';
import { SetSelectedLocale } from 'src/app/store/app/app.action';

interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
  isPrimary?: boolean;
  enabled?: boolean;
  translationNoticeKey: string;
}

const LANGUAGE_PRESENTATION: Record<
  string,
  { nativeName: string; translationNoticeKey: string }
> = {
  en: {
    nativeName: 'English',
    translationNoticeKey: 'languageSwitcher.enTranslationNotice'
  },
  fr: {
    nativeName: 'French',
    translationNoticeKey: 'languageSwitcher.frTranslationNotice'
  }
};

function toLanguageOption(locale: MsLocale): LanguageOption {
  const presentation = LANGUAGE_PRESENTATION[toTranslocoLang(locale.code)];
  return {
    code: locale.code,
    name: locale.name,
    nativeName: presentation?.nativeName ?? locale.name,
    isPrimary: locale.primary,
    enabled: true,
    translationNoticeKey: presentation?.translationNoticeKey
  };
}

export type LanguageSwitcherMode = 'popover' | 'modal';

@Component({
  selector: 'pos-language-switcher',
  templateUrl: './language-switcher.component.html',
  styleUrls: ['./language-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LanguageSwitcherComponent implements OnInit, OnDestroy {
  @Input() color = 'light';
  @Input() mode: LanguageSwitcherMode = 'popover';

  readonly showSwitcher$: Observable<boolean> = this.store.select(
    AppSelectors.isLanguageSwitcherEnabled
  );

  private readonly destroy$ = new Subject<void>();
  readonly currentLanguageCode$ = this.languageService.getCurrentLocale$;
  readonly languageOptions$: Observable<LanguageOption[]> = this.store
    .select(AppSelectors.getLocales)
    .pipe(map((locales) => locales.map(toLanguageOption)));

  selectedLanguage: string;
  private committedLanguage: string;
  triggerId = `language-trigger-${Math.random().toString(36).substr(2, 9)}`;
  isModalOpen = false;

  constructor(
    private readonly languageService: LanguageService,
    private readonly popoverController: PopoverController,
    private readonly modalController: ModalController,
    private readonly store: Store
  ) {}

  ngOnInit() {
    this.store
      .select(AppSelectors.getSelectedLocaleCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((localeCode) => {
        this.selectedLanguage = localeCode;
        this.committedLanguage = localeCode;
      });
  }

  get hasPendingLanguageChange(): boolean {
    return (
      !!this.selectedLanguage &&
      this.selectedLanguage !== this.committedLanguage
    );
  }

  onLanguageChange(languageCode: string | Event) {
    // Handle both string and event object
    this.selectedLanguage =
      typeof languageCode === 'string'
        ? languageCode
        : (languageCode as RadioGroupCustomEvent<string>).detail.value;
  }

  selectLanguage(languageCode: string) {
    this.selectedLanguage = languageCode;
  }

  async openModal() {
    if (this.mode !== 'modal') {
      return;
    }
    this.isModalOpen = true;
  }

  async closeModal() {
    this.modalController.dismiss();
    this.isModalOpen = false;
  }

  private async closePopover() {
    // Close the popover
    const popover = await this.popoverController.getTop();
    if (popover) {
      await popover.dismiss();
    }
  }

  async applyLanguageSelection() {
    if (this.selectedLanguage) {
      const locale = this.store
        .selectSnapshot(AppSelectors.getLocales)
        .find(({ code }) => code === this.selectedLanguage);
      if (locale) {
        this.store.dispatch(new SetSelectedLocale(locale));
      }

      // Close the appropriate interface
      if (this.mode === 'modal') {
        await this.closeModal();
      } else {
        await this.closePopover();
      }
    }
  }

  async ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    // Ensure any open popover/modal is dismissed when component is destroyed
    await this.closePopover();
    if (this.isModalOpen) {
      await this.closeModal();
    }
  }
}
